import { Box, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys, onboardingKeys } from "../../../../api/queryKeys";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import { IntegrationsMapping } from "../IntegrationsMapping";
import MicrosoftAzureCloudConfigPage from "../MicrosoftAzureCloud/MicrosoftAzureCloudConfigPage";
import MicrosoftAzureNewIntegration from "./MicrosoftAzureNewIntegration";
import MicrosoftAzureRegisteredIntegration from "./MicrosoftAzureRegisteredIntegration";

const MicrosoftAzureIntegration = ({ integrationName, guideMode = false }) => {
  const { isIntegrated, isIntegratedLoading } =
    useIsIntegrated(integrationName);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [windowsDefenderAtpPermissions, setWindowsDefenderAtpPermissions] =
    useState([]);
  const [windows365OfficePermissions, setWindows365OfficePermissions] =
    useState([]);
  const [registeredMicrosoftIntegrations, setRegisteredMicrosoftIntegrations] =
    useState([]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const {
    registerMicrosoftOffice365Integration,
    getMicrosoftOffice365Permissions,
    registerIntuneIntegration,
    registerMicrosoftDefenderEndpointIntegration,
    registerMicrosoftEntraIDIntegration,
    getIntunePermissions,
    getMicrosoftEntraIDPermissions,
    getMicrosoftDefenderEndpointPermissions,
    getRegisteredMicrosoftIntegrations,
    registerMicrosoftAzureCloudIntegration,
    getMicrosoftAzureCloudPermissions,
  } = useAPI();
  const queryClient = useQueryClient();

  const integrationDisplayName =
    IntegrationsMapping[integrationName].displayName;

  const INTEGRATION_IMAGES = {
    intune: logos.IntuneLogo,
    microsoft_defender_endpoint: logos.MicrosoftDefenderEndpoint,
    microsoft_entra_id: logos.MicrosoftEntraIDLogo,
    microsoft_office_365: logos.MicrosoftOffice365Logo,
    azure: logos.MicrosoftAzureCloudLogo,
  };

  useEffect(() => {
    const getRegisteredIntegrations = async () => {
      try {
        const response = await getRegisteredMicrosoftIntegrations();
        setRegisteredMicrosoftIntegrations(response.data);
      } catch (error) {
        console.error(error);
      }
    };
    getRegisteredIntegrations().then(() => {});
  }, []);

  useEffect(() => {
    const getPermissions = async () => {
      try {
        let response;
        if (integrationName === "intune") {
          response = await getIntunePermissions();
          setPermissions(response.data);
        } else if (integrationName === "microsoft_defender_endpoint") {
          response = await getMicrosoftDefenderEndpointPermissions();
          const { graph, windows_defender_atp } = response.data;
          setPermissions(graph);
          setWindowsDefenderAtpPermissions(windows_defender_atp);
        } else if (integrationName === "microsoft_entra_id") {
          response = await getMicrosoftEntraIDPermissions();
          setPermissions(response.data);
        } else if (integrationName === "microsoft_office_365") {
          response = await getMicrosoftOffice365Permissions();
          const { graph, office_365_exchange_online } = response.data;
          setPermissions(graph);
          setWindows365OfficePermissions(office_365_exchange_online);
        } else if (integrationName === "azure") {
          response = await getMicrosoftAzureCloudPermissions();
          setPermissions(response.data);
        }
      } catch (error) {
        console.error(error);
      }
    };
    getPermissions();
  }, []);

  const onSubmit = (values) => {
    setLoading(true);

    const integrationFunctions = {
      intune: registerIntuneIntegration,
      microsoft_defender_endpoint: registerMicrosoftDefenderEndpointIntegration,
      microsoft_entra_id: registerMicrosoftEntraIDIntegration,
      microsoft_office_365: registerMicrosoftOffice365Integration,
      azure: registerMicrosoftAzureCloudIntegration,
    };

    const registerIntegration = async () => {
      try {
        const integrationFunction = integrationFunctions[integrationName];
        if (!integrationFunction) {
          throw new Error("Unknown integration name");
        }
        const response = await integrationFunction(
          values.clientSecret.trim(),
          values.clientId.trim(),
          values.tenantId.trim(),
          values.is_gov_tenant
        );

        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        // only in EDR integrations, we need to invalidate the onboarding context
        queryClient.invalidateQueries({
          queryKey: onboardingKeys.all,
        });
        Toast.success(`${integrationDisplayName} integrated successfully`);
      } catch (error) {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      } finally {
        setLoading(false);
      }
    };
    registerIntegration();
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? `Once integrated, the Breeze platform will automatically read the configurations from ${integrationDisplayName} and prioritize the suggested remediation.`
            : ""
        }
        image={INTEGRATION_IMAGES[integrationName] || null}
        iconBorderRadius={0}
        name={integrationDisplayName}
        content={
          <>
            {(loading || isIntegratedLoading) && <LoadingIcon size={25} />}
            <Box
              sx={{
                display: loading ? "none" : "block",
              }}
            >
              {isIntegrated && !guideMode ? (
                integrationName === "azure" ? (
                  <>
                    <MicrosoftAzureCloudConfigPage />
                  </>
                ) : (
                  <Box>
                    <Typography variant={"h4"}>
                      {integrationDisplayName} is integrated!
                    </Typography>
                  </Box>
                )
              ) : registeredMicrosoftIntegrations.length > 0 ? (
                <MicrosoftAzureRegisteredIntegration
                  integrationName={integrationName}
                  registeredMicrosoftIntegrations={
                    registeredMicrosoftIntegrations
                  }
                  permissions={permissions}
                  windowsDefenderAtpPermissions={windowsDefenderAtpPermissions}
                  windows365OfficePermissions={windows365OfficePermissions}
                  setLoading={setLoading}
                  guideMode={guideMode}
                ></MicrosoftAzureRegisteredIntegration>
              ) : (
                <MicrosoftAzureNewIntegration
                  integrationName={integrationName}
                  integrationDisplayName={integrationDisplayName}
                  permissions={permissions}
                  windowsDefenderAtpPermissions={windowsDefenderAtpPermissions}
                  windows365OfficePermissions={windows365OfficePermissions}
                  onSubmit={onSubmit}
                  onExpanded={addToExpandedList}
                  handleExpandAll={handleExpandAll}
                  handleCollapseAll={handleCollapseAll}
                  addToExpandedList={addToExpandedList}
                  guideMode={guideMode}
                ></MicrosoftAzureNewIntegration>
              )}
            </Box>
          </>
        }
      />
    </Box>
  );
};

export default MicrosoftAzureIntegration;
